import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ExternalLink, MapPin, Loader2, BedDouble, Home } from "lucide-react";

export type DashboardProperty = {
  id: string;
  listing_url: string | null;
  status: string | null;
  created_at: string | null;
  property_name: string | null;
  owner_website: string | null;
  image_url: string | null;
  city: string | null;
  country: string | null;
  is_verified?: boolean;
  bedrooms: number | null;
  property_type: string | null;
};

interface Props {
  property: DashboardProperty;
  index: number;
}

const DashboardPropertyCard = ({ property, index }: Props) => {
  const isVerified = property.is_verified === true && !!property.owner_website;
  const location = [property.city, property.country].filter(Boolean).join(", ");

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group"
    >
      <div
        className="flex flex-col rounded-xl border border-border bg-card overflow-hidden shadow-[0_1px_3px_0_hsl(var(--foreground)/0.04)] hover:shadow-lg transition-shadow"
      >
        {/* Clickable Image */}
        <Link to={`/listings/${property.id}`} className="relative aspect-video overflow-hidden bg-muted block cursor-pointer">
          {property.image_url ? (
            <img
              src={property.image_url}
              alt={property.property_name || "Property"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-xs text-muted-foreground">No photo</span>
            </div>
          )}

          {/* Status badge overlay */}
          <div className="absolute top-2.5 right-2.5">
            {isVerified ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-primary text-primary-foreground shadow-sm">
                <CheckCircle2 className="w-3 h-3" />
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-card/90 text-muted-foreground backdrop-blur-sm shadow-sm">
                <Loader2 className="w-3 h-3 animate-spin" />
                Researching
              </span>
            )}
          </div>
        </Link>

        {/* Content */}
        <div className="flex-1 flex flex-col p-4 space-y-3">
          <div>
            <Link to={`/listings/${property.id}`} className="cursor-pointer">
              <h3 className="font-display text-sm font-bold text-foreground leading-snug line-clamp-2 hover:text-primary transition-colors">
                {property.property_name || "Unnamed Property"}
              </h3>
            </Link>

            <div className="flex flex-col gap-1 mt-1.5 text-xs text-muted-foreground">
              {location && (
                <span className="flex items-center gap-1.5 min-w-0">
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span className="truncate">{location}</span>
                </span>
              )}
              <div className="flex items-center gap-3">
                {property.property_type && (
                  <span className="flex items-center gap-1.5">
                    <Home className="w-3 h-3 shrink-0" />
                    {property.property_type}
                  </span>
                )}
                {property.bedrooms && (
                  <span className="flex items-center gap-1.5">
                    <BedDouble className="w-3 h-3 shrink-0" />
                    {property.bedrooms} {property.bedrooms === 1 ? "Bed" : "Beds"}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1" />

          {isVerified ? (
            <span
              onClick={(e) => {
                e.preventDefault();
                window.open(property.owner_website!, "_blank", "noopener,noreferrer");
              }}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/85 active:scale-[0.98] transition-all duration-150 shadow-sm hover:shadow-md"
            >
              View Direct Link
              <ExternalLink className="w-3 h-3" />
            </span>
          ) : (
            <div className="relative h-1 rounded-full bg-muted overflow-hidden">
              <div className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-primary/40 animate-pulse" />
            </div>
          )}

          <span
            onClick={(e) => {
              e.preventDefault();
              window.open(property.listing_url || "https://airbnb.com", "_blank", "noopener,noreferrer");
            }}
            className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          >
            Original Airbnb Listing
            <ExternalLink className="w-2.5 h-2.5" />
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardPropertyCard;
